import React from "react";
import { useTranslation } from "react-i18next";
import { BadgeButton } from "src/components/base/badge";
import { Subheading } from "src/components/base/heading";
import { Divider } from "src/components/base/divider";
import { LinkProps } from "src/components/base/link";
import ReadOnlyACLs from "src/components/acl";
import ReadOnlyRuleSets from "src/components/rulesets";
import { FullAccessRule, FullAccessRuleDst, FullRulesetInstance } from "src/api/generated/frontend_internal_v1";

/**
 * The visual frame used for every row group on the inbound/outbound ACL tabs.
 *
 * The two ACL pages otherwise repeat the exact same `<div className="rounded
 * border p-3 …">` wrapper four times each. This card centralises the surface
 * tokens (radius, ring, shadow) and the optional "Rulesets" sub-section so the
 * pages stay focussed on data shape rather than styling.
 */
export function AnchorCard({
    badgeLabel,
    badgeHref,
    badgeParams,
    children,
    rulesets,
}: {
    badgeLabel: string;
    badgeHref: LinkProps["href"];
    badgeParams: LinkProps["params"];
    /** Primary content — usually `<ReadOnlyACLs>`. */
    children?: React.ReactNode;
    /** Rendered below the rules under a sub-heading, when present. */
    rulesets?: React.ReactNode;
}) {
    const [t] = useTranslation("admin-device-view");
    return (
        <section
            className={
                "flex flex-col gap-5 rounded-(--radius-card) bg-(--surface-card) p-5 shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 dark:ring-white/10"
            }
        >
            <BadgeButton color={"blue"} href={badgeHref} params={badgeParams} className={"self-start"}>
                {badgeLabel}
            </BadgeButton>
            {children}
            {rulesets && (
                <>
                    <Divider />
                    <Subheading>{t("heading.rulesets")}</Subheading>
                    <div className={"flex flex-col gap-4"}>{rulesets}</div>
                </>
            )}
        </section>
    );
}

/** The rules anchored to a device through one of its associated entities (tag, group, subnet, device group) */
type Anchor = {
    uuid: string;
    name: string;
    access_rules: Array<FullAccessRule>;
    rulesets: Array<FullRulesetInstance>;
};

/** The properties for {@link AnchorCardList} */
export type AnchorCardListProps<A extends Anchor> = {
    /** The anchors to render one {@link AnchorCard} each for */
    anchors: A[];

    /** The label prefix identifying the anchors' kind, e.g. `"group"` */
    prefix: string;

    /** The route of an anchor's own ACLs page */
    href: LinkProps["href"];

    /** Builds the path params for `href` */
    params: (anchor: A) => LinkProps["params"];

    /** Whether the cards show the device's inbound or outbound rules */
    direction: "inbound" | "outbound";

    /** Wraps an anchor into the ACLs' destination (inbound) resp. source (outbound) */
    aclAnchor: (anchor: A) => FullAccessRuleDst;
};

/**
 * One {@link AnchorCard} per anchor, each showing the anchor's access rules
 * and ruleset instances.
 *
 * The inbound and outbound ACL tabs render one of these per entity kind.
 */
export function AnchorCardList<A extends Anchor>(props: AnchorCardListProps<A>) {
    const { anchors, prefix, href, params, direction, aclAnchor } = props;

    return (
        <>
            {anchors.map((anchor) => (
                <AnchorCard
                    key={anchor.uuid}
                    badgeLabel={`${prefix}:${anchor.name}`}
                    badgeHref={href}
                    badgeParams={params(anchor)}
                    rulesets={
                        anchor.rulesets.length > 0
                            ? anchor.rulesets.map((instance) =>
                                  direction === "inbound" ? (
                                      <ReadOnlyRuleSets
                                          key={instance.ruleset.uuid}
                                          name={instance.ruleset.name}
                                          description={instance.ruleset.description}
                                          rules={instance.ruleset.rules}
                                          destinations={[instance.src]}
                                      />
                                  ) : (
                                      <ReadOnlyRuleSets
                                          key={instance.ruleset.uuid}
                                          name={instance.ruleset.name}
                                          description={instance.ruleset.description}
                                          rules={instance.ruleset.rules}
                                          sources={[instance.dst]}
                                      />
                                  ),
                              )
                            : undefined
                    }
                >
                    {anchor.access_rules.length !== 0 &&
                        (direction === "inbound" ? (
                            <ReadOnlyACLs rules={anchor.access_rules} dst={aclAnchor(anchor)} />
                        ) : (
                            <ReadOnlyACLs rules={anchor.access_rules} src={aclAnchor(anchor)} />
                        ))}
                </AnchorCard>
            ))}
        </>
    );
}
